/**
 * Color Palettes - Material Design 3.0
 *
 * Matches the color system from ui-react-native-core.
 * All colors are also available as CSS custom properties in config.theme-colors.css
 *
 * Color naming follows Material Design tonal palette:
 * - 0: Lightest (white or near-white)
 * - 50: Medium light
 * - 100: Brand color / Darkest
 *
 * @see https://m3.material.io/styles/color/the-color-system/key-colors-tones
 */

/**
 * Primary Color Palette
 * Brand color: #21409A (Primary Blue)
 */
export const primaryPalette = {
    primary0: '#FFFFFF',
    primary5: '#FEFBFF',
    primary10: '#FAF8FF',
    primary15: '#EFF0FF',
    primary20: '#DCE1FF',
    primary25: '#B6C4FF',
    primary30: '#8FA7FF',
    primary35: '#718CEA',
    primary40: '#5772CE',
    primary50: '#3C58B3',
    primary60: '#2F4CA6',
    primary70: '#203F99',
    primary80: '#0F338E',
    primary90: '#00287E',
    primary95: '#001E66',
    primary98: '#00164F',
    primary99: '#000D37',
    primary100: '#21409A',
} as const;

/**
 * Secondary Color Palette
 * Brand color: #22C4F4 (Cyan/Turquoise)
 */
export const secondaryPalette = {
    secondary0: '#FAFCFF',
    secondary5: '#F3FAFF',
    secondary10: '#E3F7FF',
    secondary15: '#DFF4FF',
    secondary20: '#A9E3FF',
    secondary25: '#BBE9FF',
    secondary30: '#6FCFFF',
    secondary35: '#5FD4FF',
    secondary40: '#36CEFE',
    secondary50: '#22C4F4',
    secondary60: '#009DC5',
    secondary70: '#0081A3',
    secondary80: '#006B87',
    secondary90: '#004D63',
    secondary95: '#003B4D',
    secondary98: '#002A37',
    secondary99: '#001F29',
    secondary100: '#22C4F4',
} as const;

/**
 * Tertiary Color Palette
 * Brand color: #FFCC32 (Yellow/Gold)
 */
export const tertiaryPalette = {
    tertiary0: '#FFFBFF',
    tertiary5: '#FFF8F1',
    tertiary10: '#FFEFCF',
    tertiary15: '#FFE4A6',
    tertiary20: '#FFDF92',
    tertiary25: '#FFD35C',
    tertiary30: '#F2C024',
    tertiary35: '#E5B520',
    tertiary40: '#D3A500',
    tertiary50: '#B38B00',
    tertiary60: '#937300',
    tertiary70: '#FFCC32',
    tertiary80: '#674F00',
    tertiary90: '#594400',
    tertiary95: '#4B3900',
    tertiary98: '#3E2E00',
    tertiary99: '#312400',
    tertiary100: '#FFCC32',
} as const;

/**
 * Neutral Color Palette
 * Grayscale for surfaces, backgrounds, text
 */
export const neutralPalette = {
    neutral0: '#FFFFFF',
    neutral5: '#FEFBFF',
    neutral10: '#E4E1E6',
    neutral15: '#F2F0F4',
    neutral20: '#C8C6CA',
    neutral25: '#D8D6DA',
    neutral30: '#ACAAAF',
    neutral35: '#B8B6BA',
    neutral40: '#919094',
    neutral50: '#77767A',
    neutral60: '#5E5E62',
    neutral70: '#47464A',
    neutral80: '#303034',
    neutral85: '#252529',
    neutral90: '#1B1B1F',
    neutral95: '#121316',
    neutral98: '#0D0E11',
    neutral99: '#080809',
    neutral100: '#000000',
} as const;

/**
 * Neutral Variant Palette
 * Used for surfaces with slight color tint
 */
export const neutralVariantPalette = {
    neutralVariant0: '#FFFFFF',
    neutralVariant5: '#FAF8FF',
    neutralVariant10: '#F0F0FA',
    neutralVariant15: '#E2E1EC',
    neutralVariant20: '#C6C6D0',
    neutralVariant25: '#AAAAB4',
    neutralVariant30: '#90909A',
    neutralVariant35: '#767680',
    neutralVariant40: '#5D5E67',
    neutralVariant50: '#51525B',
    neutralVariant60: '#45464F',
    neutralVariant70: '#3A3B43',
    neutralVariant80: '#2F3038',
    neutralVariant90: '#24252D',
    neutralVariant95: '#1A1B23',
    neutralVariant98: '#0F1118',
    neutralVariant100: '#000000',
} as const;

/**
 * Error/Destructive Color Palette
 * For error states, destructive actions
 */
export const errorPalette = {
    error0: '#FFFBFF',
    error5: '#FCE4E4',
    error10: '#F9C8C9',
    error15: '#FFBAB2',
    error20: '#F6ACAD',
    error25: '#FF9490',
    error30: '#F39091',
    error35: '#F07E80',
    error40: '#F07475',
    error50: '#ED1C24',
    error60: '#D4180D',
    error70: '#BB140A',
    error80: '#A21008',
    error90: '#890C06',
    error95: '#710802',
    error98: '#590501',
    error99: '#410300',
    error100: '#710802',
} as const;

/**
 * Destructive Color Palette (alias for Error)
 * Semantic naming for delete/remove actions
 */
export const destructivePalette = {
    destructive0: '#FFFBFF',
    destructive5: '#FCE4E4',
    destructive10: '#F9C8C9',
    destructive15: '#FFBAB2',
    destructive20: '#F6ACAD',
    destructive25: '#FF9490',
    destructive30: '#F39091',
    destructive35: '#F07E80',
    destructive40: '#F07475',
    destructive50: '#ED1C24',
    destructive60: '#D4180D',
    destructive70: '#BB140A',
    destructive80: '#A21008',
    destructive90: '#890C06',
    destructive95: '#710802',
    destructive98: '#590501',
    destructive99: '#410300',
    destructive100: '#710802',
} as const;

/**
 * Success Color Palette
 * For success states, confirmations
 */
export const successPalette = {
    success0: '#F0FDF0',
    success5: '#E0F7E0',
    success10: '#C8F0C8',
    success15: '#B0E8B0',
    success20: '#98E098',
    success25: '#80D880',
    success30: '#68D068',
    success35: '#60C140',
    success40: '#54B435',
    success50: '#4AA030',
    success60: '#408C2B',
    success70: '#367826',
    success80: '#2C6421',
    success90: '#22501C',
    success95: '#183C17',
    success100: '#0E2812',
} as const;

/**
 * Warning Color Palette
 * For warning states, caution alerts
 */
export const warningPalette = {
    warning0: '#FFFBF0',
    warning5: '#FFF5E0',
    warning10: '#FFEFC8',
    warning15: '#FFE8B0',
    warning20: '#FFE198',
    warning25: '#FFDA80',
    warning30: '#FFD368',
    warning35: '#FFCC50',
    warning40: '#FFC538',
    warning50: '#FF9800',
    warning60: '#E68900',
    warning70: '#CC7A00',
    warning80: '#B36B00',
    warning90: '#995C00',
    warning95: '#804D00',
    warning100: '#663E00',
} as const;

/**
 * Info Color Palette
 * For informational states, neutral alerts
 */
export const infoPalette = {
    info0: '#F0F8FF',
    info5: '#E3F2FF',
    info10: '#BBDEFB',
    info15: '#90CAF9',
    info20: '#64B5F6',
    info25: '#42A5F5',
    info30: '#2196F3',
    info40: '#1E88E5',
    info50: '#1976D2',
    info60: '#1565C0',
    info70: '#0D47A1',
    info80: '#0A3D8F',
    info90: '#07337D',
    info95: '#04296B',
    info100: '#011F59',
} as const;

/**
 * Accent Orange Palette
 * For promotional highlights, special actions
 */
export const accentOrangePalette = {
    accentOrange0: '#FFF5F0',
    accentOrange5: '#FFEBE0',
    accentOrange10: '#FFD6C0',
    accentOrange15: '#FFC2A0',
    accentOrange20: '#FFAD80',
    accentOrange25: '#FF9960',
    accentOrange30: '#FF8552',
    accentOrange40: '#FF7132',
    accentOrange50: '#E66320',
    accentOrange60: '#CC5610',
    accentOrange70: '#B34900',
    accentOrange80: '#993C00',
    accentOrange90: '#802F00',
    accentOrange95: '#662200',
    accentOrange100: '#4D1500',
} as const;

/**
 * Soft Accent Palette
 * For soft promotional elements, subtle highlights
 */
export const softAccentPalette = {
    softAccent0: '#FFFBFC',
    softAccent5: '#FFF5F7',
    softAccent10: '#FFEEF2',
    softAccent15: '#FFE7EC',
    softAccent20: '#FFE1E9',
    softAccent25: '#FFDCE6',
    softAccent30: '#FFD6E0',
    softAccent40: '#FFCAD5',
    softAccent50: '#E5B5C0',
    softAccent60: '#CCA0AB',
    softAccent70: '#B38B96',
    softAccent80: '#997681',
    softAccent90: '#80616C',
    softAccent95: '#664C57',
    softAccent100: '#4D3742',
} as const;

/**
 * Slate Color Palette
 * Cool gray with blue undertone
 * For UI elements: text, icons, borders, backgrounds
 */
export const slatePalette = {
    slate0: '#FFFFFF',
    slate5: '#F8F8F9',
    slate10: '#EDEDEF',
    slate15: '#E5E5E8',
    slate20: '#D9DADD',
    slate25: '#CDCFD2',
    slate30: '#C1C2C7',
    slate35: '#B4B5BC',
    slate40: '#A5A6AE',
    slate50: '#8E8F98',
    slate60: '#777882',
    slate70: '#5F606A',
    slate80: '#434657',
    slate90: '#2D2F3D',
    slate95: '#1A1C27',
    slate100: '#0A0B12',
} as const;

/**
 * Overlay Color Palette
 * Semi-transparent colors for overlays, backdrops
 */
export const overlayPalette = {
    overlayBlack70: 'rgba(1, 1, 1, 0.70)',
    overlayBlack50: 'rgba(0, 0, 0, 0.50)',
    overlayBlack30: 'rgba(0, 0, 0, 0.30)',
    overlayWhite70: 'rgba(255, 255, 255, 0.70)',
    overlayWhite50: 'rgba(255, 255, 255, 0.50)',
} as const;

/**
 * Combined Color Palettes
 * All color palettes in a single object
 */
export const colorsPalettes = {
    primary: primaryPalette,
    secondary: secondaryPalette,
    tertiary: tertiaryPalette,
    neutral: neutralPalette,
    neutralVariant: neutralVariantPalette,
    error: errorPalette,
    destructive: destructivePalette,
    success: successPalette,
    warning: warningPalette,
    info: infoPalette,
    accentOrange: accentOrangePalette,
    softAccent: softAccentPalette,
    slate: slatePalette,
    overlay: overlayPalette,
} as const;

/**
 * Core Brand Colors
 * Primary brand colors used across the design system
 */
export const coreColors = {
    primary: '#21409A',
    secondary: '#22C4F4',
    tertiary: '#FFCC32',
    error: '#ED1C24',
    success: '#54B435',
    warning: '#FF9800',
    info: '#2196F3',
} as const;

/**
 * Extended Colors
 * Additional semantic colors for specific use cases
 */
export const extendedColors = {
    // Accent colors
    accentOrange: '#FF7132',
    softAccent: '#FFDCE6',

    // State colors
    success: '#54B435',
    successLight: '#60C140',
    successContainer: '#C8F0C8',
    onSuccess: '#FFFFFF',
    onSuccessContainer: '#0E2812',

    warning: '#FF9800',
    warningLight: '#FFB74D',
    warningContainer: '#FFEFC8',
    onWarning: '#FFFFFF',
    onWarningContainer: '#663E00',

    info: '#2196F3',
    infoLight: '#64B5F6',
    infoContainer: '#BBDEFB',
    onInfo: '#FFFFFF',
    onInfoContainer: '#011F59',

    // Surface colors
    background: '#FAF8FF',
    surface: '#FAF8FF',
    surfaceVariant: '#E1E1F0',
    outline: '#757683',
    outlineVariant: '#C5C5D4',

    // Inverse colors
    inverseSurface: '#2F3036',
    inverseOnSurface: '#F1F0F8',
    inversePrimary: '#B6C4FF',

    // Misc
    shadow: '#000000',
    scrim: '#000000',
} as const;

/**
 * Semantic Color Tokens
 * Colors with semantic meaning for UI components
 */
export const semanticColors = {
    // Text colors
    textPrimary: neutralPalette.neutral90,
    textSecondary: neutralPalette.neutral60,
    textTertiary: neutralPalette.neutral50,
    textDisabled: neutralPalette.neutral40,
    textInverse: neutralPalette.neutral0,

    // Background colors
    backgroundPrimary: neutralPalette.neutral0,
    backgroundSecondary: neutralPalette.neutral10,
    backgroundTertiary: neutralVariantPalette.neutralVariant10,
    backgroundDisabled: neutralPalette.neutral20,

    // Border colors
    borderDefault: neutralPalette.neutral20,
    borderFocused: primaryPalette.primary100,
    borderError: errorPalette.error50,
    borderSuccess: successPalette.success40,

    // Interactive colors
    interactiveDefault: primaryPalette.primary100,
    interactiveHover: primaryPalette.primary80,
    interactivePressed: primaryPalette.primary90,
    interactiveDisabled: neutralPalette.neutral40,
} as const;

/**
 * UI Element Semantic Colors
 * Specific semantic colors for common UI elements
 */
export const uiElementColors = {
    // Text colors
    textSecondary: '#434657',
    textMuted: neutralPalette.neutral50,
    textDisabled: neutralPalette.neutral35,

    // Icon colors
    iconSecondary: '#B4B5BC',
    iconMuted: neutralPalette.neutral40,
    iconDisabled: neutralPalette.neutral20,

    // Form element colors
    inputBorder: '#D9DADD',
    inputBorderHover: neutralPalette.neutral35,
    inputBorderFocus: primaryPalette.primary100,
    inputBackground: '#EDEDEF',
    inputBackgroundHover: neutralPalette.neutral15,

    // Option/Selection colors
    optionBackground: '#EDEDEF',
    optionBackgroundHover: neutralPalette.neutral10,
    optionBackgroundSelected: primaryPalette.primary10,
    optionBorder: '#D9DADD',

    // Overlay colors
    overlayDark: '#010101B2',
    overlayLight: 'rgba(255, 255, 255, 0.7)',
    overlayModal: 'rgba(0, 0, 0, 0.5)',
} as const;

/**
 * Combined colors object
 */
export const colors = {
    palettes: colorsPalettes,
    core: coreColors,
    extended: extendedColors,
    semantic: semanticColors,
    uiElements: uiElementColors,
} as const;

// Type exports
export type PrimaryPalette = typeof primaryPalette;
export type SecondaryPalette = typeof secondaryPalette;
export type TertiaryPalette = typeof tertiaryPalette;
export type NeutralPalette = typeof neutralPalette;
export type NeutralVariantPalette = typeof neutralVariantPalette;
export type ErrorPalette = typeof errorPalette;
export type DestructivePalette = typeof destructivePalette;
export type SuccessPalette = typeof successPalette;
export type WarningPalette = typeof warningPalette;
export type InfoPalette = typeof infoPalette;
export type AccentOrangePalette = typeof accentOrangePalette;
export type SoftAccentPalette = typeof softAccentPalette;
export type SlatePalette = typeof slatePalette;
export type OverlayPalette = typeof overlayPalette;
export type ColorsPalettes = typeof colorsPalettes;
export type CoreColors = typeof coreColors;
export type ExtendedColors = typeof extendedColors;
export type SemanticColors = typeof semanticColors;
export type UiElementColors = typeof uiElementColors;
export type Colors = typeof colors;
